import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, CheckCircle } from 'lucide-react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { submitContactForm } from '../data/mock';
import { toast } from 'sonner';

const ContactForm = () => {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!executeRecaptcha) {
      toast.error('reCAPTCHA non disponible');
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Obtenir le token reCAPTCHA
      const token = await executeRecaptcha('contact_form');
      
      // Ajouter le token aux données du formulaire
      const dataWithToken = {
        ...formData,
        recaptchaToken: token
      };
      
      const result = await submitContactForm(dataWithToken);
      if (result.success) {
        // Rediriger vers la page de confirmation avec les données du contact
        navigate('/confirmation-contact', {
          state: {
            contactData: {
              ...formData,
              id: result.id || `CONTACT-${Date.now()}`
            }
          }
        });
      }
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
      <div className="mb-6">
        <div className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold mb-3">
          Contact rapide
        </div>
        <h3 className="text-2xl font-bold text-slate-900 mb-2">
          Une question générale ?
        </h3>
        <p className="text-gray-600">
          Posez-nous votre question, nous vous répondons sous 24h.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
            Nom complet <span className="text-[#FF6600]">*</span>
          </label>
          <Input
            id="name"
            name="name"
            type="text"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="Votre nom"
            className="w-full"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
            Email <span className="text-[#FF6600]">*</span>
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={handleChange}
            placeholder="votre@email.fr"
            className="w-full"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
            Téléphone <span className="text-[#FF6600]">*</span>
          </label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            required
            value={formData.phone}
            onChange={handleChange}
            placeholder="01 XX XX XX XX"
            className="w-full"
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
            Message <span className="text-[#FF6600]">*</span>
          </label>
          <Textarea
            id="message"
            name="message"
            required
            value={formData.message}
            onChange={handleChange}
            placeholder="Décrivez votre besoin..."
            rows={5}
            className="w-full"
          />
        </div>

        <div className="text-xs text-gray-400 text-center">
          Ce site est protégé par reCAPTCHA. La <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#FF6600] hover:underline">politique de confidentialité</a> et les <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-[#FF6600] hover:underline">conditions d'utilisation</a> de Google s'appliquent.
        </div>

        <Button 
          type="submit" 
          disabled={isSubmitting}
          className="w-full bg-[#020d2a] hover:bg-[#020d2a]/90 text-white font-semibold py-3"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Envoi en cours...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Envoyer le message
            </>
          )}
        </Button>
      </form>
    </div>
  );
};

export default ContactForm;
